import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, Users, Send, Loader2, Search, Filter, CheckSquare, Square,
  BarChart3, Target, Calendar, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import { publicUsersApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const MarketingPage = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterConsent, setFilterConsent] = useState('all'); // all, consented, not-consented
  const [composeOpen, setComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailForm, setEmailForm] = useState({
    subject: '',
    preview_text: '',
    body: ''
  });

  // Plantillas de email prediseñadas
  const emailTemplates = [
    {
      id: 'new_event',
      name: '🎉 Nuevo Evento',
      subject: '¡Nueva fecha anunciada! No te lo pierdas',
      preview_text: 'Conseguí tus entradas antes que se agoten',
      body: `¡Hola!

Tenemos una noticia que te va a encantar: **¡NUEVA FECHA CONFIRMADA!**

📅 [NOMBRE DEL EVENTO]
📍 [UBICACIÓN]
🎫 Entradas disponibles desde $[PRECIO]

Las primeras tandas siempre vuelan, así que no te duermas.

👉 [LINK A ENTRADAS]

Nos vemos en la pista,
**El equipo de Bravo Producciones**`
    },
    {
      id: 'last_tickets',
      name: '🔥 Últimas Entradas',
      subject: '⚠️ Quedan pocas entradas para [EVENTO]',
      preview_text: 'No te quedes afuera',
      body: `¡Ey!

Esto es una alerta oficial: **las entradas para [EVENTO] se están agotando**.

Solo quedan [X] lugares disponibles y no queremos que te lo pierdas.

🎫 Asegurá tu lugar ahora: [LINK]

La cuenta regresiva empezó...

**Bravo Producciones**`
    },
    {
      id: 'aftermovie',
      name: '🎬 Aftermovie',
      subject: '¡Ya está el aftermovie de [EVENTO]!',
      preview_text: 'Reviví la mejor noche',
      body: `¿Te acordás de esa noche épica?

Ya subimos el **aftermovie oficial** de [EVENTO] y queremos que lo veas.

▶️ [LINK AL VIDEO]

Si te encontrás, taggeanos en Instagram @bravoproducciones 📸

¿Listo para la próxima? Seguinos para enterarte primero de las nuevas fechas.

**Bravo Producciones**`
    },
    {
      id: 'gallery',
      name: '📸 Fotos Disponibles',
      subject: 'Las fotos de [EVENTO] ya están disponibles',
      preview_text: 'Descargá tus fotos de la noche',
      body: `¡Hola!

Las fotos oficiales de **[EVENTO]** ya están en nuestra galería.

📸 Ver galería: [LINK]

**¿Sos suscriptor FULL BRAVX?** Descargá todas las fotos en HD sin costo adicional.

**¿Todavía no sos BRAVX?** Suscribite y accedé a beneficios exclusivos.

Gracias por ser parte,
**Bravo Producciones**`
    },
    {
      id: 'fullbravx_promo',
      name: '👑 Promo FULL BRAVX',
      subject: '¡Oferta especial FULL BRAVX solo por hoy!',
      preview_text: 'Beneficios exclusivos te esperan',
      body: `¡Esta es tu oportunidad!

Solo por tiempo limitado, suscribite a **FULL BRAVX** y obtené:

✅ Acceso anticipado a entradas
✅ Descuentos exclusivos
✅ Fotos HD sin marca de agua
✅ Contenido exclusivo

🔥 **[OFERTA ESPECIAL]**

👉 Suscribite ahora: [LINK]

La oferta termina pronto...

**Bravo Producciones**`
    },
    {
      id: 'custom',
      name: '✏️ Personalizado',
      subject: '',
      preview_text: '',
      body: ''
    }
  ];

  const canView = hasPermission('marketing.view');
  const canSendEmails = hasPermission('marketing.send');

  useEffect(() => {
    if (canView) {
      fetchUsers();
    }
  }, [canView]);

  const fetchUsers = async () => {
    try {
      const res = await publicUsersApi.adminList({ limit: 500 });
      // Response format: { users: [], total: N, limit: N, skip: N }
      setUsers(res.data?.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesConsent = filterConsent === 'all' || 
      (filterConsent === 'consented' && user.email_consent) ||
      (filterConsent === 'not-consented' && !user.email_consent);
    
    return matchesSearch && matchesConsent;
  });

  const consentedUsers = users.filter(u => u.email_consent);
  const notConsentedUsers = users.filter(u => !u.email_consent);

  // Helper to get user ID (handles both 'id' and 'user_id' formats)
  const getUserId = (user) => user.user_id || user.id;

  const toggleSelectUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const selectAll = () => {
    const visibleIds = filteredUsers.map(u => getUserId(u));
    setSelectedUsers(visibleIds);
  };

  const deselectAll = () => {
    setSelectedUsers([]);
  };

  const selectConsented = () => {
    const consentedIds = consentedUsers.map(u => getUserId(u));
    setSelectedUsers(consentedIds);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template.id);
    setEmailForm({
      subject: template.subject,
      preview_text: template.preview_text,
      body: template.body
    });
  };

  const handleSendEmails = async () => {
    if (!emailForm.subject || !emailForm.body) {
      toast.error('Completá asunto y contenido');
      return;
    }

    if (selectedUsers.length === 0) {
      toast.error('Seleccioná al menos un destinatario');
      return;
    }

    setSending(true);
    try {
      const response = await publicUsersApi.sendMarketingEmail({
        user_ids: selectedUsers,
        subject: emailForm.subject,
        preview_text: emailForm.preview_text,
        body: emailForm.body
      });
      
      const { sent, failed } = response.data;
      
      if (failed === 0) {
        toast.success(`✅ ${sent} email(s) enviado(s) correctamente`);
      } else if (sent > 0) {
        toast.warning(`${sent} enviado(s), ${failed} fallido(s)`);
      } else {
        toast.error(`Error: ${failed} email(s) fallaron`);
      }
      
      setComposeOpen(false);
      setEmailForm({ subject: '', preview_text: '', body: '' });
      setSelectedTemplate(null);
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error sending emails:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar emails');
    } finally {
      setSending(false);
    }
  };

  if (!canView) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center h-64 text-[#A1A1AA]">
            No tenés permisos para ver esta sección
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6 lg:p-8 space-y-6"
          data-testid="marketing-page"
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <Mail className="text-[#D4AF37]" size={28} />
                Marketing
              </h1>
              <p className="text-zinc-500 text-sm mt-1">
                Envía comunicaciones a tus usuarios
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <a href="/admin/campanas/nueva" data-testid="marketing-new-campaign-btn">
                <Button className="bg-gradient-to-r from-[#D4AF37] to-[#F5D25A] hover:from-[#F5D25A] hover:to-[#D4AF37] text-black font-bold">
                  <Send size={16} className="mr-2" />
                  Crear Campaña (a un segmento)
                </Button>
              </a>
              {canSendEmails && selectedUsers.length > 0 && (
                <Button
                  onClick={() => setComposeOpen(true)}
                  variant="outline"
                  className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  data-testid="compose-email-btn"
                >
                  <Send size={16} className="mr-2" />
                  Enviar a selección ({selectedUsers.length})
                </Button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Users className="text-[#D4AF37]" size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Total</p>
                  <p className="metric-value-gold text-xl">{users.length}</p>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckSquare className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Con Permiso</p>
                  <p className="text-xl font-bold text-green-400">{consentedUsers.length}</p>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-zinc-700/30 flex items-center justify-center">
                  <Square className="text-zinc-500" size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Sin Permiso</p>
                  <p className="text-xl font-bold text-zinc-400">{notConsentedUsers.length}</p>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Target className="text-blue-400" size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Seleccionados</p>
                  <p className="text-xl font-bold text-blue-400">{selectedUsers.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Info Banner */}
          <div className="admin-card !bg-[#D4AF37]/5 !border-[#D4AF37]/30 p-4">
            <p className="text-sm text-[#D4AF37]">
              <strong>Importante:</strong> Solo se recomienda enviar emails comerciales a usuarios que dieron su consentimiento.
              Enviar a usuarios sin consentimiento puede violar regulaciones de privacidad (GDPR, Ley 25.326).
            </p>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" size={18} />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por email o nombre..."
                className="pl-10 input-bravo"
              />
            </div>
            <select
              value={filterConsent}
              onChange={(e) => setFilterConsent(e.target.value)}
              className="bg-[#121212] border border-[#27272A] px-4 py-2 text-white"
            >
              <option value="all">Todos</option>
              <option value="consented">Con consentimiento</option>
              <option value="not-consented">Sin consentimiento</option>
            </select>
            <div className="flex gap-2">
              <Button
                onClick={selectConsented}
                variant="outline"
                className="border-[#27272A] text-green-500"
                size="sm"
              >
                Seleccionar con consentimiento
              </Button>
              <Button
                onClick={selectAll}
                variant="outline"
                className="border-[#27272A]"
                size="sm"
              >
                Todos
              </Button>
              <Button
                onClick={deselectAll}
                variant="outline"
                className="border-[#27272A]"
                size="sm"
              >
                Ninguno
              </Button>
            </div>
          </div>

          {/* Users List */}
          <div className="bg-[#121212] border border-[#27272A] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0A0A0A]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-[#52525B]">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onChange={() => selectedUsers.length === filteredUsers.length ? deselectAll() : selectAll()}
                        className="accent-[#D4AF37]"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-[#52525B]">Email</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-[#52525B]">Nombre</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-[#52525B]">Consentimiento</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-[#52525B]">Registro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A]">
                  {filteredUsers.map((user) => (
                    <tr 
                      key={user.user_id} 
                      className={`hover:bg-[#1A1A1A] transition-colors cursor-pointer ${
                        selectedUsers.includes(getUserId(user)) ? 'bg-[#D4AF37]/5' : ''
                      }`}
                      onClick={() => toggleSelectUser(getUserId(user))}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(getUserId(user))}
                          onChange={() => toggleSelectUser(getUserId(user))}
                          onClick={(e) => e.stopPropagation()}
                          className="accent-[#D4AF37]"
                        />
                      </td>
                      <td className="px-4 py-3 text-white">{user.email}</td>
                      <td className="px-4 py-3 text-[#A1A1AA]">{user.full_name}</td>
                      <td className="px-4 py-3">
                        {user.email_consent ? (
                          <span className="inline-flex items-center gap-1 text-green-500 text-xs">
                            <CheckSquare size={14} />
                            Sí
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#52525B] text-xs">
                            <Square size={14} />
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#52525B] text-sm">
                        {user.created_at ? format(new Date(user.created_at), 'dd MMM yyyy', { locale: es }) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredUsers.length === 0 && (
              <div className="p-8 text-center text-[#52525B]">
                No se encontraron usuarios
              </div>
            )}
          </div>

          {/* Compose Email Dialog */}
          <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#09090B] border-[#27272A]">
              <DialogHeader>
                <DialogTitle className="text-xl font-black uppercase tracking-tight flex items-center gap-3">
                  <Send className="text-[#D4AF37]" />
                  Redactar Email
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div className="bg-[#1A1A1A] p-3 rounded-lg">
                  <p className="text-sm text-[#A1A1AA]">
                    <strong className="text-white">{selectedUsers.length}</strong> destinatarios seleccionados
                  </p>
                </div>

                {/* Template Selection */}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-3 block">
                    Elegir Plantilla
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {emailTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleSelectTemplate(template)}
                        className={`p-3 text-left text-sm border transition-all hover:scale-[1.02] ${
                          selectedTemplate === template.id
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-white'
                            : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B] bg-[#121212]'
                        }`}
                      >
                        <span className="font-medium">{template.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-[#27272A] pt-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">Asunto *</Label>
                    <Input
                      value={emailForm.subject}
                      onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                      placeholder="Ej: ¡Nueva fecha anunciada!"
                      className="input-bravo mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">Texto de Vista Previa</Label>
                  <Input
                    value={emailForm.preview_text}
                    onChange={(e) => setEmailForm({...emailForm, preview_text: e.target.value})}
                    placeholder="Texto que aparece en la bandeja de entrada..."
                    className="input-bravo mt-2"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#A1A1AA]">Contenido *</Label>
                  <textarea
                    value={emailForm.body}
                    onChange={(e) => setEmailForm({...emailForm, body: e.target.value})}
                    placeholder="Escribe el contenido del email...

Usá **texto** para negrita y [PLACEHOLDER] para datos a reemplazar."
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 text-white focus:outline-none focus:border-[#D4AF37] min-h-[200px] mt-2 font-mono text-sm"
                  />
                  <p className="text-xs text-[#52525B] mt-2">
                    Tip: Usá **texto** para negrita. Los [PLACEHOLDERS] deben ser reemplazados antes de enviar.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-[#27272A]">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setComposeOpen(false);
                      setSelectedTemplate(null);
                      setEmailForm({ subject: '', preview_text: '', body: '' });
                    }}
                    className="border-[#27272A]"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSendEmails}
                    disabled={sending || !emailForm.subject || !emailForm.body}
                    className="btn-primary"
                  >
                    {sending ? (
                      <Loader2 size={16} className="animate-spin mr-2" />
                    ) : (
                      <Send size={16} className="mr-2" />
                    )}
                    Enviar a {selectedUsers.length} usuarios
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default MarketingPage;
